import R from 'ramda';
import { useEffect, useState } from 'react';

import { Crop } from './types';

const multiplySize = (coefficient: number, size: { width: number; height: number }) => {
  return {
    width: coefficient * size.width,
    height: coefficient * size.height,
  };
};

export const calculateCropArea = (
  imageSize: { width: number; height: number },
  crop: Crop,
): { x: number; y: number; width: number; height: number } => {
  const imageAspect = imageSize.width / imageSize.height;
  const cropSize = multiplySize(
    1 / crop.zoom,
    imageAspect > crop.aspect
      ? { width: imageSize.height * crop.aspect, height: imageSize.height }
      : { width: imageSize.width, height: imageSize.width / crop.aspect },
  );
  const left = crop.position.x * (imageSize.width - cropSize.width);
  const top = crop.position.y * (imageSize.height - cropSize.height);
  const right = left + cropSize.width;
  const bottom = top + cropSize.height;
  const x = Math.round(left);
  const y = Math.round(top);
  return {
    x,
    y,
    width: Math.round(right - x),
    height: Math.round(bottom - y),
  };
};

export const getImageSize = (imageUrl: string): Promise<{ width: number; height: number }> =>
  new Promise<{ width: number; height: number }>((resolve, reject) => {
    const image = new Image();
    image.src = imageUrl;
    image.addEventListener('load', () => resolve({ width: image.width, height: image.height }));
    image.addEventListener('error', error => reject(error));
  });

export const restrictCrop = (crop: Crop): Crop => {
  return {
    ...crop,
    position: {
      x: R.clamp(0, 1, crop.position.x),
      y: R.clamp(0, 1, crop.position.y),
    },
  };
};

export const useImageSize = (imageUrl: string): { width: number; height: number } | undefined => {
  const [imageSize, setImageSize] = useState<{ width: number; height: number } | undefined>(undefined);
  useEffect(() => {
    let cancelled = false;
    const measureImageSize = async () => {
      try {
        const size = await getImageSize(imageUrl);
        if (!cancelled) {
          setImageSize(size);
        }
      } catch {
        if (!cancelled) {
          setImageSize(undefined);
        }
      }
    };
    measureImageSize();
    return () => {
      cancelled = true;
    };
  }, [imageUrl]);

  return imageSize;
};

export async function cropImage(file: File, crop: Crop): Promise<Blob> {
  const imageUrl = URL.createObjectURL(file);
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', error => reject(error));
    image.crossOrigin = 'anonymous';
    image.src = imageUrl;
  });
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;

  const cropArea = calculateCropArea({ width: image.width, height: image.height }, crop);
  canvas.width = cropArea.width;
  canvas.height = cropArea.height;

  ctx.drawImage(image, cropArea.x, cropArea.y, cropArea.width, cropArea.height, 0, 0, cropArea.width, cropArea.height);

  return new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(blob => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('Failed to convert canvas to blob'));
      }
    }, file.type),
  );
}
